// Dynamics and a score's form: barlines, repeats, endings, segno/coda jumps, tempo changes,
// measure repeats, slashes and key changes. Shared by the editor, renderer, playback and server.
import { KEY_SPECS } from "./piano";

export const DYNAMICS = ["ppp", "pp", "p", "mp", "mf", "f", "ff", "fff", "sfz", "rfz", "fp", "pf"];
// Loudness for playback (0-1). Accents add to it; sfz and rfz are one loud note.
export const DYNAMIC_LEVELS = { ppp: .15, pp: .25, p: .35, mp: .45, mf: .55, f: .7, ff: .85, fff: 1 };
export const HAIRPINS = ["cresc", "dim"];
// Text at a measure's end, in VexFlow's Repetition names, with their labels.
export const JUMPS = {
  fine: ["FINE", "Fine"], dc: ["DC", "D.C."], dcAlFine: ["DC_AL_FINE", "D.C. al Fine"], dcAlCoda: ["DC_AL_CODA", "D.C. al Coda"],
  ds: ["DS", "D.S."], dsAlFine: ["DS_AL_FINE", "D.S. al Fine"], dsAlCoda: ["DS_AL_CODA", "D.S. al Coda"], toCoda: ["TO_CODA", "To Coda"],
};
// Signs at a measure's start.
export const MARKS = { segno: ["SEGNO_LEFT", "Segno"], coda: ["CODA_LEFT", "Coda"] };
export const TEMPO_CHANGES = { accel: "accel.", rit: "rit." };
// accel. and rit. ramp the tempo across their measure by this much, and it stays there.
export const TEMPO_CHANGE_RATIO = { accel: 1.25, rit: 0.8 };
export const BARLINES = ["double", "final"];
export const SLASHES = ["beat", "rhythm"];
export const MAX_REPEAT_TIMES = 8, MAX_ENDING = 4;

// The key in effect at a measure: the latest key change at or before it, else the score's key.
export function keyAt(score, measureIndex) {
  for (let index = Math.min(measureIndex, (score?.measures?.length || 0) - 1); index >= 0; index -= 1) {
    if (score.measures[index]?.keySig) return score.measures[index].keySig;
  }
  return score?.keySig || "C";
}

const sameEnding = (a, b) => Array.isArray(a) && Array.isArray(b) && a.join() === b.join();
// Where a measure sits in its ending's bracket: "begin", "mid", "end", "single" or null.
export function endingSpan(measures, index) {
  const ending = measures[index]?.ending;
  if (!ending?.length) return null;
  const before = sameEnding(measures[index - 1]?.ending, ending), after = sameEnding(measures[index + 1]?.ending, ending);
  return before && after ? "mid" : before ? "end" : after ? "begin" : "single";
}

// The order measures play in. Repeats play `repeatTimes` (default 2) times, taking the
// ending for each pass. D.C. and D.S. jump back once, after which repeats are not taken and
// only the last ending plays; "al Fine" stops at Fine and "al Coda" leaves at To Coda for
// the coda. Capped so a malformed score can't loop for ever.
export function playbackOrder(measures, limit = 4096) {
  const order = [], plays = new Map();
  const segno = measures.findIndex((measure) => measure.mark === "segno");
  const coda = measures.findIndex((measure) => measure.mark === "coda");
  // The highest ending number in each run of endings, which is the one played after a jump.
  const lastEnding = measures.map((_, index) => {
    if (!measures[index].ending?.length) return 0;
    let start = index, end = index;
    while (measures[start - 1]?.ending?.length) start -= 1;
    while (measures[end + 1]?.ending?.length) end += 1;
    return Math.max(...measures.slice(start, end + 1).flatMap((measure) => measure.ending));
  });
  let index = 0, sectionStart = 0, pass = 1, jumped = null;
  while (index < measures.length && order.length < limit) {
    const measure = measures[index];
    if (measure.repeatStart && !jumped && index !== sectionStart) { sectionStart = index; pass = 1; }
    const takeEnding = !measure.ending?.length || (jumped ? measure.ending.includes(lastEnding[index]) : measure.ending.includes(pass));
    if (!takeEnding) { index += 1; continue; }
    order.push(index);
    if (jumped === "fine" && measure.jump === "fine") break;
    if (jumped === "coda" && measure.jump === "toCoda" && coda > index) { index = coda; continue; }
    if (measure.repeatEnd && !jumped) {
      const played = plays.get(index) || 1;
      if (played < (measure.repeatTimes || 2)) { plays.set(index, played + 1); pass = played + 1; index = sectionStart; continue; }
      pass = 1;
      sectionStart = index + 1;
    }
    if (!jumped && /^d[cs]/.test(measure.jump || "")) {
      jumped = /AlFine$/.test(measure.jump) ? "fine" : /AlCoda$/.test(measure.jump) ? "coda" : "end";
      index = measure.jump.startsWith("ds") && segno >= 0 ? segno : 0;
      continue;
    }
    index += 1;
  }
  return order;
}

// The measure whose notes a measure plays: a measure repeat (%) or beat slashes play the
// last written-out measure before them.
export function soundingMeasure(measures, index) {
  let source = index;
  while (source > 0 && (measures[source].measureRepeat || measures[source].slashes === "beat")) source -= 1;
  return source;
}

// The form markings a measure may carry, validated; `fail(message)` throws for bad values.
export function measureMarkings(measure, fail) {
  const result = {};
  const check = (ok, message) => { if (!ok) fail(message); };
  if (measure.barline != null) { check(BARLINES.includes(measure.barline), "Choose a valid barline."); result.barline = measure.barline; }
  for (const key of ["repeatStart", "repeatEnd", "measureRepeat"]) if (measure[key] != null) { check(typeof measure[key] === "boolean", "Invalid repeat."); if (measure[key]) result[key] = true; }
  if (measure.repeatTimes != null) {
    check(Number.isInteger(measure.repeatTimes) && measure.repeatTimes >= 2 && measure.repeatTimes <= MAX_REPEAT_TIMES, `A repeat plays 2 to ${MAX_REPEAT_TIMES} times.`);
    result.repeatTimes = measure.repeatTimes;
  }
  if (measure.ending != null) {
    check(Array.isArray(measure.ending) && measure.ending.length && measure.ending.length <= MAX_ENDING
      && measure.ending.every((number) => Number.isInteger(number) && number >= 1 && number <= MAX_ENDING), "Invalid ending.");
    result.ending = [...new Set(measure.ending)].sort((a, b) => a - b);
  }
  if (measure.mark != null) { check(Object.hasOwn(MARKS, measure.mark), "Invalid segno or coda."); result.mark = measure.mark; }
  if (measure.jump != null) { check(Object.hasOwn(JUMPS, measure.jump), "Invalid D.C., D.S. or Fine."); result.jump = measure.jump; }
  if (measure.tempoChange != null) { check(Object.hasOwn(TEMPO_CHANGES, measure.tempoChange), "Invalid tempo change."); result.tempoChange = measure.tempoChange; }
  if (measure.slashes != null) { check(SLASHES.includes(measure.slashes), "Invalid slashes."); result.slashes = measure.slashes; }
  if (measure.keySig != null) { check(KEY_SPECS.includes(measure.keySig), "Choose a valid key signature."); result.keySig = measure.keySig; }
  if (measure.rehearsal != null) { check(typeof measure.rehearsal === "string" && /^[A-Za-z0-9]{1,3}$/.test(measure.rehearsal), "Invalid rehearsal mark."); result.rehearsal = measure.rehearsal; }
  if (measure.tempo != null) { check(Number.isInteger(measure.tempo) && measure.tempo >= 20 && measure.tempo <= 400, "A tempo mark is 20 to 400 BPM."); result.tempo = measure.tempo; }
  return result;
}
